<template>
    <div class="addPaymentMethod">
        <form-message v-if="stripeError" :message="stripeError" />
        <form-message
            v-else-if="failedPlanUpgrade"
            :message="`We noticed you tried to upgrade to the ${subscription.plan.name} plan. However, we failed to charge your payment
            method on file. Please add a new payment method.`"
        />
        <form-message
            v-else-if="failedMemberAdd"
            message="We noticed you tried to add another member. However, we failed to charge your payment method on file. Please add a new
            payment method."
        />
        <form-dropdown
            v-if="false"
            id="dropdown-type"
            v-model="form.type"
            label="Choose Payment Method"
            :settings="dropdownSettings"
        />

        <template v-if="form.type === 'default'">
            <form-input
                v-model="form.name"
                label="Card Holder Name"
                placeholder="Card Holder Name"
                :error="form.errors.get('name')"
            />

            <div class="field">
                <label class="label"> Card Details </label>
                <div class="control">
                    <div id="create-card" class="input" />
                </div>
            </div>
            <div v-if="!hasNotBillingSources && showCheckbox" class="paymentCheckboxWrap">
                <div v-if="!backup" class="paymentCheckboxWrap__item">
                    <checkbox
                        :label="'Set as Primary'"
                        :value="isSuspended || isUnpaid ? true : form.primary"
                        :disabled="isSuspended || isUnpaid"
                        @handle-change="togglePrimary"
                    />
                </div>
                <div class="paymentCheckboxWrap__item">
                    <checkbox
                        :label="'Set as Backup'"
                        :value="form.primary ? false : backup ? backup : form.backup"
                        :disabled="isSuspended || isUnpaid || backup"
                        @handle-change="toggleBackup"
                    />
                </div>
            </div>
        </template>
        <template v-if="form.type === 'ach'">
            <template v-if="form.confirm">
                <div class="field">Confirm your payment method</div>
            </template>
            <template v-else>
                <form-input
                    v-model="form.name"
                    label="Holder Name"
                    placeholder="Holder Name"
                    :error="form.errors.get('name')"
                />

                <form-input
                    v-model="form.email"
                    label="Email"
                    placeholder="Email"
                    :error="form.errors.get('email')"
                />
            </template>
        </template>
    </div>
</template>

<script>
import { mapState } from 'pinia';
import stores from 'store/modules';
import Form from 'core/Form';
import cards from '../../mixins/cards';
import OrganizationMixin from '../../mixins/organization';

export default {
    emits: ['closed'],
    
    mixins: [cards, OrganizationMixin],

    props: {
        form: { type: Object, required: true },
        backup: { type: Boolean, default: false },
        showCheckbox: { type: Boolean, default: true },
    },

    data: () => ({
        stripeForm: new Form(
            {
                name: '',
                country: 'US',
                city: '',
                region: '',
                zip: '',
            },
            false
        ),
        clientSecret: null,
    }),

    computed: {
        optionsPaymentMethodTypes() {
            return [
                {
                    id: 'default',
                    label: 'Default',
                    value: 'default',
                },
                {
                    id: 'ach',
                    label: 'ACH',
                    value: 'ach',
                },
            ];
        },

        dropdownSettings() {
            return {
                valueField: 'id',
                searchField: ['label'],
                options: this.optionsPaymentMethodTypes,
                highlight: true,
                render: {
                    item: this.dropdownItem,
                    option: this.dropdownOption,
                },
                maxItems: 1,
                dropdownParent: 'body',
            };
        },

        failedPlanUpgrade() {
            return this.isUnpaid && this.subscription.suspended_for === 'failed-plan-upgrade';
        },

        failedMemberAdd() {
            return this.isUnpaid && this.subscription.suspended_for === 'failed-member-add';
        },

        ...mapState(stores.BillingSources, ['billingSources', 'billingSourcesSimple']),
    },

    watch: {
        backup() {
            if (this.backup) {
                this.form.primary = false;
                this.form.backup = true;
            }
        },

        'form.type'() {
            this.init();
        },
    },

    methods: {
        async init() {
            if (this.form.type === 'default') {
                this.initPaymentMethodDefault();
            }

            if (this.form.type === 'ach') {
                this.initPaymentMethodAch();
            }
        },

        reset() {
            this.form.reset();
            this.stripeError = null;
            this.clientSecret = null;
        },

        async initPaymentMethodDefault() {
            if (this.isSuspended || this.isUnpaid || !this.billingSources.length) {
                this.form.primary = true;
            }

            this.$nextTick(() => {
                this.initStripe();
                this.initStripeCard('#create-card');
            });
        },

        async initPaymentMethodAch() {
            this.clientSecret = await this.$store.dispatch('createBillingSetupIntentMicroservice');

            this.initStripe();
        },

        togglePrimary() {
            this.form.primary = !this.form.primary;

            if (this.form.primary) {
                this.form.backup = false;
            }
        },

        toggleBackup() {
            this.form.backup = !this.form.backup;

            if (this.form.backup) {
                this.form.primary = false;
            }
        },

        async save() {
            let response = null;
            if (this.form.type === 'default') {
                response = await this.handleSavePaymentMethodDefault();
            }

            if (this.form.type === 'ach') {
                if (this.form.confirm) {
                    await this.handleConfirmPaymentMethodAch();
                } else {
                    await this.handleSavePaymentMethodAch();
                }
            }

            return {
                stripeError: this.stripeError,
                formErrors: this.form.errors.any(),
                response,
            };
        },

        async handleSavePaymentMethodDefault() {
            this.form.busy = true;
            this.stripeError = null;

            if (!this.form.name) {
                this.form.errors.record({ name: ['Card Holder Name is required'] });
                this.form.busy = false;
            } else {
                this.form.errors.clear();
            }

            try {
                const result = await this.stripe.createToken(this.stripeCard, {
                    name: `${this.stripeForm.name.trim()}`,
                    address_city: this.stripeForm.city,
                    address_state: this.stripeForm.region,
                    address_country: this.stripeForm.country,
                });

                if (result.error) throw result;
                if (!this.form.name) {
                    this.form.busy = false;

                    return;
                }

                this.form.card_token = result.token.id;

                const response = await this.$store.dispatch('createBillingSources', this.form);

                if (this.form.backup || this.backup) {
                    this.$store.dispatch('setBackupSourceId', response.id);
                }

                return response;
            } catch (e) {
                if (e.error) {
                    this.stripeError = e.error.message;
                } else {
                    this.stripeError =
                        e.message || 'Failed to save your payment method, please try again.';
                }

                this.form.busy = false;
            }
        },

        async handleSavePaymentMethodAch() {
            try {
                this.form.busy = true;
                this.stripeError = null;

                const { setupIntent, error } = await this.stripe.collectBankAccountForSetup({
                    clientSecret: this.clientSecret,
                    params: {
                        payment_method_type: 'us_bank_account',
                        payment_method_data: {
                            billing_details: {
                                name: this.form.name,
                                email: this.form.email,
                            },
                        },
                    },
                    expand: ['payment_method'],
                });

                if (error) {
                    this.stripeError = error.message;
                } else if (setupIntent.status === 'requires_payment_method') {
                    this.stripeError =
                        'Customer canceled the hosted verification modal. Present them with other payment method type options.';
                } else if (setupIntent.status === 'requires_confirmation') {
                    this.form.confirm = true;
                }
            } catch (e) {
                this.$log.error(e, this.$options.__file, 'handleSavePaymentMethodAch');
            } finally {
                this.form.busy = false;
            }
        },

        async handleConfirmPaymentMethodAch() {
            try {
                this.form.busy = true;
                this.stripeError = null;

                const { setupIntent, error } = await this.stripe.confirmUsBankAccountSetup(
                    this.clientSecret
                );

                if (error) {
                    this.stripeError = error.message;
                } else if (setupIntent.status === 'requires_payment_method') {
                    this.stripeError =
                        'Confirmation failed. Attempt again with a different payment method.';
                } else if (setupIntent.status === 'succeeded') {
                    this.form.successful = true;

                    setTimeout(() => {
                        this.closed();
                    }, 1500);
                } else if (
                    setupIntent.next_action &&
                    setupIntent.next_action.type === 'verify_with_microdeposits'
                ) {
                    this.stripeError = `
                        The account needs to be verified via microdeposits.
                        Display a message to consumer with next steps (consumer waits for
                        microdeposits, then enters a statement descriptor code on a page sent to them via email).
                    `;

                    this.stripe.verifyMicrodepositsForSetup(this.clientSecret, {
                        // Provide either a descriptor_code OR amounts, not both
                        descriptor_code: 'SMT86W',
                        amounts: [32, 45],
                    });
                }
            } catch (e) {
                this.$log.error(e, this.$options.__file, 'handleConfirmPaymentMethodAch');
            } finally {
                this.form.busy = false;
                await this.$store.dispatch('getBillingSources', {
                    useLoading: false,
                });
            }
        },

        dropdownItem(item, escape) {
            return `<div class="member-option is-selected">
                        <div class="member-option-info">
                            ${escape(item.label)}
                        </div>
                    </div>`;
        },

        dropdownOption(item, escape) {
            return `<div class="selectize-dropdown-option">
                        <div class="selectize-dropdown-option-info">
                            <span class="label">${escape(item.label)}</span>
                        </div>
                    </div>`;
        },

        closed() {
            this.$emit('closed');
        },
    },

    mounted() {
        this.init();
    },
};
</script>
